import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import type { DeletedCheckStatus, DeletedContact, GhlAppStatus, LocationAiProfile, LocationSummary, Note } from '../types';
import { Connections } from './Connections';
import { RowMenu } from '../components/RowMenu';
import { daysAgo, formatDateTime, formatNumber, formatRelative, toDateInput } from '../format';
import { Dialog } from '../components/Dialog';
import { LocationSummaryPanel } from '../components/AiSummary';

interface Props {
  locations: LocationSummary[];
  /** POST /locations/ is agency-only, so the button is disabled without one. */
  canCreate: boolean;
  /** Edit level on this tab: connect, add notes, change flags. */
  canEdit: boolean;
  /** Can open Settings to set the marketplace app up. */
  canOpenSettings: boolean;
  onOpenSettings: () => void;
  onRefresh: () => void;
  onConnectionsChanged: () => void;
}

export function Subaccounts({
  locations,
  canCreate,
  canEdit,
  canOpenSettings,
  onOpenSettings,
  onRefresh,
  onConnectionsChanged,
}: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [summaryId, setSummaryId] = useState<string | null>(null);
  const [setupId, setSetupId] = useState<string | null>(null);
  const [deletedId, setDeletedId] = useState<string | null>(null);

  const selected = locations.find((location) => location.id === selectedId) ?? null;

  return (
    <div className="stack">
      <MarketplaceConnect canEdit={canEdit} canOpenSettings={canOpenSettings} onOpenSettings={onOpenSettings} />

      <div className="card">
        <div className="card-head">
          <h2>Sub-accounts</h2>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setCreating(true)}
            disabled={!canCreate || !canEdit}
            title={
              canCreate
                ? undefined
                : 'Creating a sub-account is agency-only in HighLevel. Connect the marketplace app or add an agency token below.'
            }
          >
            + Add sub-account
          </button>
        </div>
        <p className="card-sub">
          {canCreate
            ? 'Mirrored from your agency via GET /locations/search. '
            : 'Each sub-account here was connected with its own token (below). '}
          “Active client” is what the dashboard filters narrow to by default; “Tracked” controls
          whether it is synced at all. A sub-account with no token is skipped by syncs rather than
          reported as a failure.
        </p>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Location</th>
                <th className="num">Calls</th>
                <th className="num">Answered</th>
                <th>Last call</th>
                <th>Last sync</th>
                <th className="num">Notes</th>
                <th>Connected</th>
                <th>Active client</th>
                <th>Tracked</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {locations.map((location) => (
                <tr key={location.id}>
                  <td>
                    <span className="cell-strong">{location.name}</span>
                    <div className="cell-sub">{location.id}</div>
                  </td>
                  <td>
                    {[location.city, location.state, location.country].filter(Boolean).join(', ') || '—'}
                    {location.timezone ? <div className="cell-sub">{location.timezone}</div> : null}
                  </td>
                  <td className="num">{formatNumber(location.totalCalls)}</td>
                  <td className="num">{formatNumber(location.answeredCalls)}</td>
                  <td className="nowrap">{formatDateTime(location.lastCallAt)}</td>
                  <td className="nowrap">
                    {formatRelative(location.lastSyncAt)}
                    {location.lastSyncStatus === 'error' ? (
                      <div className="cell-sub" style={{ color: 'var(--danger-text)' }} title={location.lastSyncError ?? ''}>
                        sync failed
                      </div>
                    ) : null}
                  </td>
                  <td className="num">{formatNumber(location.noteCount)}</td>
                  <td>
                    <span className="tag">
                      <span
                        className="dot"
                        style={{
                          background: location.connected
                            ? 'var(--outcome-answered)'
                            : 'var(--outcome-pending)',
                        }}
                        aria-hidden="true"
                      />
                      {location.connected ? 'Yes' : 'No token'}
                    </span>
                  </td>
                  <td>
                    <label className="row small">
                      <input
                        type="checkbox"
                        disabled={!canEdit}
                        checked={location.isActiveClient}
                        onChange={async (event) => {
                          await api.setLocationFlags(location.id, { isActiveClient: event.target.checked });
                          onRefresh();
                        }}
                      />
                      {location.isActiveClient ? 'Active' : 'Inactive'}
                    </label>
                  </td>
                  <td>
                    <label className="row small">
                      <input
                        type="checkbox"
                        disabled={!canEdit}
                        checked={location.trackingEnabled}
                        onChange={async (event) => {
                          await api.setLocationFlags(location.id, { trackingEnabled: event.target.checked });
                          onRefresh();
                        }}
                      />
                      {location.trackingEnabled ? 'On' : 'Off'}
                    </label>
                  </td>
                  <td className="nowrap">
                    <RowMenu
                      label={`Actions for ${location.name}`}
                      items={[
                        {
                          label: (
                            <>
                              <span className="ai-spark" aria-hidden="true">✦</span> AI summary
                            </>
                          ),
                          onSelect: () => setSummaryId(location.id),
                        },
                        {
                          label: (
                            <>
                              AI setup
                              {location.aiSetup.context || location.aiSetup.prompt ? (
                                <span className="ai-spark" aria-label="customised"> ●</span>
                              ) : null}
                            </>
                          ),
                          title: "This sub-account's business and lead rating prompt",
                          onSelect: () => setSetupId(location.id),
                        },
                        { label: `Notes${location.noteCount ? ` (${location.noteCount})` : ''}`, onSelect: () => setSelectedId(location.id) },
                        {
                          label: 'Deleted contacts',
                          title: 'Contacts the dashboard knows that were deleted in HighLevel',
                          onSelect: () => setDeletedId(location.id),
                        },
                        {
                          label: 'Sync now',
                          disabled: !location.connected || !canEdit,
                          title: location.connected
                            ? undefined
                            : 'No credential for this sub-account. Connect the marketplace app or add its token below.',
                          onSelect: () => void api.runSync([location.id]).then(onRefresh),
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
              {locations.length === 0 ? (
                <tr>
                  <td colSpan={11} className="empty">
                    No sub-accounts yet — connect the marketplace app or add a token below.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <Connections onChanged={onConnectionsChanged} canEdit={canEdit} />

      {selected ? (
        <NotesDialog location={selected} canEdit={canEdit} onClose={() => setSelectedId(null)} onChanged={onRefresh} />
      ) : null}
      {deletedId ? (
        <DeletedContactsDialog
          location={locations.find((location) => location.id === deletedId)!}
          canCheck={canEdit}
          onClose={() => setDeletedId(null)}
        />
      ) : null}
      {setupId ? (
        <AiSetupDialog
          location={locations.find((location) => location.id === setupId)!}
          canEdit={canEdit}
          onClose={() => setSetupId(null)}
          onSaved={onRefresh}
        />
      ) : null}
      {summaryId ? (
        <SummaryDialog
          location={locations.find((location) => location.id === summaryId)!}
          onClose={() => setSummaryId(null)}
        />
      ) : null}
      {creating ? (
        <CreateLocationDialog
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            onRefresh();
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * The marketplace app: one install by the agency covers every sub-account,
 * so it is the main way in. Setting the app up (client id and secret) is
 * under Settings; connecting is here.
 */
function MarketplaceConnect({
  canEdit,
  canOpenSettings,
  onOpenSettings,
}: {
  canEdit: boolean;
  canOpenSettings: boolean;
  onOpenSettings: () => void;
}) {
  const [app, setApp] = useState<GhlAppStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .ghlApp()
      .then(setApp)
      .catch((cause) => setError(cause instanceof ApiError ? cause.message : String(cause)));
  }, []);

  const configured = Boolean(app && app.source !== 'none');
  return (
    <div className="card">
      <div className="card-head">
        <h2>Marketplace app</h2>
        {app ? (
          <span className="tag">
            <span
              className="dot"
              aria-hidden="true"
              style={{
                background: app.installed
                  ? 'var(--outcome-answered)'
                  : configured
                    ? 'var(--outcome-no_answer)'
                    : 'var(--outcome-pending)',
              }}
            />
            {app.installed ? `Connected ${formatRelative(app.installedAt)}` : configured ? 'Set up — not connected yet' : 'Not set up'}
          </span>
        ) : null}
      </div>
      <p className="card-sub">
        Connecting the agency’s marketplace app gives the dashboard every sub-account at once — no token per sub-account.
        Choose the agency and the sub-accounts on HighLevel’s screen; connect again any time to add sub-accounts.
        {app?.installed && app.companyId ? ` Connected agency: ${app.companyId}.` : ''}
      </p>
      {error ? <div className="inline-result bad">{error}</div> : null}
      <div className="row">
        {configured && canEdit ? (
          <a className="btn btn-primary" href="/api/auth/install">
            {app?.installed ? 'Reconnect / add sub-accounts' : 'Connect with marketplace app'}
          </a>
        ) : (
          <button type="button" className="btn btn-primary" disabled title={canEdit ? 'Set the app up in Settings first.' : 'You have view-only access here.'}>
            Connect with marketplace app
          </button>
        )}
        {!configured && canOpenSettings ? (
          <button type="button" className="btn" onClick={onOpenSettings}>
            Set the app up in Settings
          </button>
        ) : null}
        {!configured && !canOpenSettings ? (
          <span className="small muted">An admin needs to set the app up under Settings first.</span>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function NotesDialog({
  location,
  canEdit,
  onClose,
  onChanged,
}: {
  location: LocationSummary;
  canEdit: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [author, setAuthor] = useState('');
  const [pinned, setPinned] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .notes(location.id)
      .then((response) => setNotes(response.notes))
      .catch((cause) => setError(String(cause)));
  }, [location.id]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!body.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const response = await api.createNote(location.id, {
        body: body.trim(),
        title: title.trim() || undefined,
        author: author.trim() || undefined,
        pinned,
      });
      setNotes((current) => [response.note, ...current]);
      setTitle('');
      setBody('');
      setPinned(false);
      onChanged();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : String(cause));
    } finally {
      setSaving(false);
    }
  }

  async function remove(noteId: string) {
    await api.deleteNote(location.id, noteId);
    setNotes((current) => current.filter((note) => note.id !== noteId));
    onChanged();
  }

  async function togglePin(note: Note) {
    const response = await api.updateNote(location.id, note.id, { pinned: !note.pinned });
    setNotes((current) =>
      current
        .map((item) => (item.id === note.id ? response.note : item))
        .sort((a, b) => Number(b.pinned) - Number(a.pinned) || b.createdAt - a.createdAt),
    );
  }

  return (
    <Dialog title={`Notes · ${location.name}`} onClose={onClose}>
      {canEdit ? (
        <form onSubmit={submit} className="stack" style={{ marginBottom: 18 }}>
          <div className="dialog-grid">
            <div className="field">
              <label htmlFor="note-title">Title (optional)</label>
              <input
                id="note-title"
                type="text"
                value={title}
                maxLength={200}
                onChange={(event) => setTitle(event.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="note-author">Author (optional)</label>
              <input
                id="note-author"
                type="text"
                value={author}
                maxLength={120}
                onChange={(event) => setAuthor(event.target.value)}
              />
            </div>
            <div className="field full">
              <label htmlFor="note-body">Note</label>
              <textarea
                id="note-body"
                value={body}
                required
                maxLength={20000}
                onChange={(event) => setBody(event.target.value)}
                placeholder="What should the team know about this sub-account?"
              />
            </div>
          </div>
          <div className="spread">
            <label className="row small">
              <input type="checkbox" checked={pinned} onChange={(event) => setPinned(event.target.checked)} />
              Pin to the top
            </label>
            <button type="submit" className="btn btn-primary" disabled={saving || !body.trim()}>
              {saving ? 'Saving…' : 'Add note'}
            </button>
          </div>
          {error ? <div className="banner error">{error}</div> : null}
        </form>
      ) : null}

      <div className="stack">
        {notes.map((note) => (
          <div className="note" key={note.id}>
            <div className="note-head">
              <span className="note-title">
                {note.pinned ? '📌 ' : ''}
                {note.title ?? 'Note'}
              </span>
              <span className="note-meta">
                {note.author ? `${note.author} · ` : ''}
                {formatDateTime(note.createdAt)}
              </span>
            </div>
            <div className="note-body">{note.body}</div>
            {canEdit ? (
              <div className="note-actions">
                <button type="button" className="btn btn-sm" onClick={() => togglePin(note)}>
                  {note.pinned ? 'Unpin' : 'Pin'}
                </button>
                <button type="button" className="btn btn-sm btn-danger" onClick={() => remove(note.id)}>
                  Delete
                </button>
              </div>
            ) : null}
          </div>
        ))}
        {notes.length === 0 ? <p className="empty">No notes yet for this sub-account.</p> : null}
      </div>

      <p className="small muted" style={{ marginTop: 16 }}>
        HighLevel’s public API has no sub-account note resource — only contact notes
        (<code>/contacts/&#123;id&#125;/notes</code>) — so these are stored in this dashboard’s database.
      </p>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------

const FIELDS: { name: string; label: string; placeholder?: string; full?: boolean }[] = [
  { name: 'name', label: 'Name *', placeholder: 'Acme Mortgage' },
  { name: 'phone', label: 'Phone', placeholder: '+15551234567' },
  { name: 'address', label: 'Address', full: true },
  { name: 'city', label: 'City' },
  { name: 'state', label: 'State' },
  { name: 'country', label: 'Country (2-letter ISO)', placeholder: 'US' },
  { name: 'postalCode', label: 'Postal code' },
  { name: 'website', label: 'Website', placeholder: 'https://example.com' },
  { name: 'timezone', label: 'Timezone', placeholder: 'America/New_York' },
  { name: 'snapshotId', label: 'Snapshot ID (optional)', full: true },
];

function CreateLocationDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = Object.fromEntries(
        Object.entries(values)
          .map(([key, value]) => [key, value.trim()])
          .filter(([, value]) => value !== ''),
      ) as Record<string, string>;
      await api.createLocation(payload);
      onCreated();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : String(cause));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog title="Add sub-account" onClose={onClose}>
      <form onSubmit={submit}>
        <div className="dialog-grid">
          {FIELDS.map((field) => (
            <div className={field.full ? 'field full' : 'field'} key={field.name}>
              <label htmlFor={`new-${field.name}`}>{field.label}</label>
              <input
                id={`new-${field.name}`}
                type="text"
                required={field.name === 'name'}
                placeholder={field.placeholder}
                value={values[field.name] ?? ''}
                onChange={(event) => setValues({ ...values, [field.name]: event.target.value })}
              />
            </div>
          ))}
        </div>

        <p className="small muted" style={{ marginTop: 14 }}>
          Creates the sub-account in HighLevel via <code>POST /locations/</code>. Your install needs the
          agency-level <code>locations.write</code> scope.
        </p>

        {error ? (
          <div className="banner error" style={{ marginTop: 12 }}>
            {error}
          </div>
        ) : null}

        <div className="dialog-actions">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving || !values.name?.trim()}>
            {saving ? 'Creating…' : 'Create sub-account'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------

const SUMMARY_PERIODS: { label: string; from: () => string; to: () => string }[] = [
  { label: 'Last 7 days', from: () => daysAgo(6), to: () => toDateInput(new Date()) },
  { label: 'Last 30 days', from: () => daysAgo(29), to: () => toDateInput(new Date()) },
  { label: 'Last 90 days', from: () => daysAgo(89), to: () => toDateInput(new Date()) },
  {
    label: 'Last month',
    from: () => {
      const now = new Date();
      return toDateInput(new Date(now.getFullYear(), now.getMonth() - 1, 1));
    },
    to: () => {
      const now = new Date();
      return toDateInput(new Date(now.getFullYear(), now.getMonth(), 0));
    },
  },
];

function SummaryDialog({ location, onClose }: { location: LocationSummary; onClose: () => void }) {
  const [period, setPeriod] = useState(1);
  const chosen = SUMMARY_PERIODS[period]!;
  return (
    <Dialog title={`AI summary · ${location.name}`} onClose={onClose}>
      <div className="stack">
        <div className="segmented" role="group" aria-label="Period" style={{ alignSelf: 'flex-start' }}>
          {SUMMARY_PERIODS.map((option, index) => (
            <button type="button" key={option.label} aria-pressed={period === index} onClick={() => setPeriod(index)}>
              {option.label}
            </button>
          ))}
        </div>
        <LocationSummaryPanel locationId={location.id} from={chosen.from()} to={chosen.to()} />
      </div>
    </Dialog>
  );
}

/**
 * One sub-account's AI setup: what the client's business is (added to every
 * summary, rating and Assistant chat about it) and how its leads are rated.
 */
function AiSetupDialog({
  location,
  canEdit,
  onClose,
  onSaved,
}: {
  location: LocationSummary;
  canEdit: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [profile, setProfile] = useState<LocationAiProfile | null>(null);
  const [context, setContext] = useState('');
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    api
      .aiProfile(location.id)
      .then((next) => {
        setProfile(next);
        setContext(next.businessContext);
        setPrompt(next.ratingPrompt || next.defaultRatingPrompt);
      })
      .catch((cause) => setNote({ ok: false, text: cause instanceof ApiError ? cause.message : String(cause) }));
  }, [location.id]);

  if (!profile) {
    return (
      <Dialog title={`AI setup · ${location.name}`} onClose={onClose}>
        {note ? <div className="inline-result bad">{note.text}</div> : <p className="empty">Loading…</p>}
      </Dialog>
    );
  }

  const ownPrompt = Boolean(profile.ratingPrompt);
  const savedPrompt = profile.ratingPrompt || profile.defaultRatingPrompt;
  const contextChanged = context.trim() !== profile.businessContext.trim();
  const promptChanged = prompt.trim() !== savedPrompt.trim();

  async function save(patch: { businessContext?: string; ratingPrompt?: string }, askRerate: boolean) {
    setBusy(true);
    setNote(null);
    try {
      const next = await api.saveAiProfile(location.id, patch);
      setProfile(next);
      setContext(next.businessContext);
      setPrompt(next.ratingPrompt || next.defaultRatingPrompt);
      onSaved();
      if (askRerate && window.confirm(`Re-rate ${location.name}'s leads with the new prompt? This runs in the background, within the daily limit.`)) {
        const { marked } = await api.rerateLocation(location.id);
        setNote({ ok: true, text: `Saved. ${marked} leads will be re-rated.` });
      } else {
        setNote({ ok: true, text: 'Saved.' });
      }
    } catch (cause) {
      setNote({ ok: false, text: cause instanceof ApiError ? cause.message : String(cause) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={`AI setup · ${location.name}`} onClose={onClose}>
      <div className="stack">
        <div className="field">
          <label htmlFor="setup-context">About this business</label>
          <textarea
            id="setup-context"
            rows={5}
            maxLength={4000}
            disabled={!canEdit}
            value={context}
            placeholder="e.g. DSCR and fix-and-flip lender in Florida and Georgia. Loans from $100k to $2M on rentals and 2-4 units; no owner-occupied. Minimum 660 credit."
            onChange={(event) => setContext(event.target.value)}
          />
          <span className="small muted">
            What this client sells and to whom, the loans they fund, who is a good lead. Used in this sub-account’s
            summaries, lead ratings and Assistant chats, alongside the agency description in Settings.
          </span>
          {canEdit ? (
            <div className="row">
              <button
                type="button"
                className="btn btn-sm btn-primary"
                disabled={busy || !contextChanged}
                onClick={() => save({ businessContext: context }, false)}
              >
                Save description
              </button>
              <span className="small muted">{context.length} / 4000</span>
            </div>
          ) : null}
        </div>

        <div className="field">
          <label htmlFor="setup-prompt">Lead rating prompt</label>
          <textarea
            id="setup-prompt"
            rows={12}
            maxLength={6000}
            disabled={!canEdit}
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
          />
          <span className="small muted">
            {ownPrompt
              ? 'This sub-account has its own prompt.'
              : 'Using the default prompt from Settings → AI. Edit it here to give this sub-account its own.'}{' '}
            The lead’s data and the 0–10 answer format are added automatically.
          </span>
          {canEdit ? (
            <div className="row">
              <button
                type="button"
                className="btn btn-sm btn-primary"
                disabled={busy || !promptChanged || !prompt.trim()}
                onClick={() => save({ ratingPrompt: prompt }, true)}
              >
                Save prompt
              </button>
              {promptChanged ? (
                <button type="button" className="btn btn-sm" disabled={busy} onClick={() => setPrompt(savedPrompt)}>
                  Discard changes
                </button>
              ) : null}
              {ownPrompt ? (
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={busy}
                  onClick={() => {
                    if (window.confirm('Go back to the default prompt from Settings?')) void save({ ratingPrompt: '' }, true);
                  }}
                >
                  Use the default prompt
                </button>
              ) : null}
              <span className="small muted">{prompt.length} / 6000</span>
            </div>
          ) : null}
        </div>

        {note ? <div className={`inline-result${note.ok ? '' : ' bad'}`}>{note.text}</div> : null}
        {!canEdit ? <p className="small muted">You have view-only access to sub-accounts.</p> : null}
        <div className="dialog-actions">
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * Contacts the dashboard knows that were deleted in HighLevel: compares the
 * stored contacts with HighLevel's current list and confirms each missing one.
 */
function DeletedContactsDialog({
  location,
  canCheck,
  onClose,
}: {
  location: LocationSummary;
  canCheck: boolean;
  onClose: () => void;
}) {
  const [status, setStatus] = useState<DeletedCheckStatus | null>(null);
  const [contacts, setContacts] = useState<DeletedContact[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .deletedContacts(location.id)
      .then((response) => {
        setStatus(response.status);
        setContacts(response.contacts);
      })
      .catch((cause) => setError(cause instanceof ApiError ? cause.message : String(cause)));
  }, [location.id]);
  useEffect(load, [load]);
  useEffect(() => {
    if (!status?.running) return;
    const timer = setInterval(load, 2000);
    return () => clearInterval(timer);
  }, [status?.running, load]);

  async function check() {
    setError(null);
    try {
      const response = await api.checkDeletedContacts(location.id);
      setStatus(response.status);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : String(cause));
    }
  }

  return (
    <Dialog title={`Deleted contacts · ${location.name}`} onClose={onClose} wide>
      <div className="stack">
        <p className="small muted" style={{ margin: 0 }}>
          Compares the contacts this dashboard has stored (ones with calls, deals, appointments or messages) with the
          contacts HighLevel has now, then looks up each missing one to confirm it was deleted. Contacts deleted before
          the dashboard ever saw them can’t be listed.
        </p>
        <div className="row">
          <button type="button" className="btn btn-primary" disabled={!canCheck || status?.running} onClick={check}>
            {status?.running ? 'Checking…' : 'Check HighLevel now'}
          </button>
          {contacts && contacts.length ? (
            <a className="btn" href={api.deletedContactsCsvUrl(location.id)}>
              Download CSV
            </a>
          ) : null}
          {status ? (
            <span className="small muted">
              {status.running
                ? status.candidates
                  ? `Confirming ${formatNumber(status.checked)} of ${formatNumber(status.candidates)} missing contacts…`
                  : `Reading HighLevel’s contacts… ${formatNumber(status.liveContacts)} so far`
                : status.error
                  ? `Stopped: ${status.error}`
                  : `Last checked ${formatRelative(status.finishedAt)}: ${formatNumber(status.liveContacts)} contacts in HighLevel, ` +
                    `${formatNumber(status.storedContacts)} stored here, ${formatNumber(status.found)} newly found deleted.`}
            </span>
          ) : null}
        </div>
        {error ? <div className="inline-result bad">{error}</div> : null}
        {!contacts ? (
          <p className="empty">Loading…</p>
        ) : contacts.length === 0 ? (
          <p className="empty">{status ? 'No deleted contacts found.' : 'Not checked yet — press “Check HighLevel now”.'}</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Contact</th>
                  <th>Phone</th>
                  <th className="num">Calls</th>
                  <th>Last call</th>
                  <th>Last appointment</th>
                  <th className="num">Deals</th>
                  <th>Deleted</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact) => (
                  <tr key={contact.contactId}>
                    <td>
                      <div className="cell-strong">{contact.name ?? 'Unnamed'}</div>
                      <div className="cell-sub">{contact.email ?? contact.contactId}</div>
                    </td>
                    <td className="nowrap">{contact.phone ?? '—'}</td>
                    <td className="num">{formatNumber(contact.calls)}</td>
                    <td className="nowrap">{formatDateTime(contact.lastCallAt)}</td>
                    <td className="nowrap">{formatDateTime(contact.lastAppointmentAt)}</td>
                    <td className="num">{formatNumber(contact.deals)}</td>
                    <td className="nowrap" title={`Found gone by a check ${formatDateTime(contact.detectedAt)}`}>
                      {formatDateTime(contact.deletedAt)}
                      <div className="cell-sub">
                        {contact.deletedFrom === 'deals' ? 'when its deals were deleted' : 'found by a check (no deals)'}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="dialog-actions">
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </Dialog>
  );
}
